// Querys and Mutations
import {
    CREATE_EMPLOYEE,
    GET_WITH_PAGINATION,
    GET_ACTIVATED,
    UPDATE_STATUS,
    DELETE,
    GET_ALL_BY_ID,
    UPDATE,
    GET_NEXT_CODE,
    VALIDATE_CODE,
    UPDATE_DEVICES,
    GET_INFO_DEVICES,
    GET_SUMMARY,
    GET_ALL,
    GET_BIRTHDAYS_PER_MONTH
} from '@/graphql/config/EmployeeGraphql'

// models
import { IEmployee, EmployeeModel, EmployeesWithPaginationModel, ValidateCode, EmployeeResponse, DeviceHasEmployee, SummaryEmployee, FilterEmployeeModel } from '@/models/config'
import { ResponseCustom, ResponseDefault } from '@/models'

// apollo
import { apolloClient } from '@/apollo'

class EmployeeService {
    /**
     * Queries
     */
     async getActivated (): Promise<IEmployee[]> {
        const result = await apolloClient.query({
            query: GET_ACTIVATED
        })
        return result.data.employeesActivated
    }

    async getWithPagination (perPage: number, page: number, filters: FilterEmployeeModel): Promise<EmployeesWithPaginationModel> {
        const result = await apolloClient.query({
            query: GET_WITH_PAGINATION,
            variables: {
                page: page,
                perPage: perPage,
                filters: filters
            }
        })
        return {
            pagination: result.data.employeesWithPagination.paginatorInfo,
            objects: result.data.employeesWithPagination.data
        }
    }

    async getByID (id: number): Promise<EmployeeModel> {
        const result = await apolloClient.query({
            query: GET_ALL_BY_ID,
            variables: {
                id: id
            }
        })
        return result.data.employee
    }

    async getNextCode (): Promise<number|null> {
        const result = await apolloClient.query({
            query: GET_NEXT_CODE
        })
        if (result.data.getNextCode.code) {
            return result.data.getNextCode.code
        }
        return null
    }

    async validateCode (code: string): Promise<ValidateCode> {
        const result = await apolloClient.query({
            query: VALIDATE_CODE,
            variables: {
                input: {
                    code: Number(code)
                }
            }
        })
        return result.data.validateCodeEmployee
    }

    async getInfoDevices (id: number): Promise<DeviceHasEmployee[]> {
        const result = await apolloClient.query({
            query: GET_INFO_DEVICES,
            variables: {
                id: id
            }
        })
        return result.data.getInfoEmployeeDevices
    }

    async getSummary (): Promise<SummaryEmployee> {
        return (await apolloClient.query({
            query: GET_SUMMARY
        })).data.getSummaryEmployees
    }

    async getAll (filters: FilterEmployeeModel|null = null): Promise<IEmployee[]> {
        const result = await apolloClient.query({
            query: GET_ALL,
            variables: {
                filters: filters
            }
        })
        return result.data.employees
    }

    async getBirthdaysPerMonth (month: number): Promise<IEmployee[]> {
        const result = await apolloClient.query({
            query: GET_BIRTHDAYS_PER_MONTH,
            variables: {
                month: month
            }
        })
        return result.data.birthdaysPerMonth
    }

    /**
     * Mutations
     */
    async create (form: EmployeeModel): Promise<EmployeeResponse> {
        const result = await apolloClient.query({
            query: CREATE_EMPLOYEE,
            variables: {
                input: form
            }
        })
        if (result.errors && result.errors.length > 0) {
            return {
                response: {
                    error: true,
                    success: false,
                    msg: result.errors[0].message
                },
                data: null
            }
        }
        return {
            response: {
                error: false,
                success: true,
                msg: ''
            },
            data: result.data.createEmployee
        }
    }

    async update (id: number, form: EmployeeModel): Promise<EmployeeResponse> {
        const result = await apolloClient.query({
            query: UPDATE,
            variables: {
                id: id,
                input: form
            }
        })
        if (result.errors && result.errors.length > 0) {
            return {
                response: {
                    error: true,
                    success: false,
                    msg: result.errors[0].message
                },
                data: null
            }
        }
        return {
            response: {
                error: false,
                success: true,
                msg: ''
            },
            data: result.data.updateEmployee
        }
    }

    async changeStatus (id: number, status: boolean): Promise<EmployeeModel> {
        const result = await apolloClient.query({
            query: UPDATE_STATUS,
            variables: {
                id: id,
                input: {
                    is_active: status
                }
            }
        })
        return result.data.updateStatusEmployee
    }

    async delete (id: number): Promise<ResponseDefault> {
        await apolloClient.query({
            query: DELETE,
            variables: {
                id: id
            }
        })
        return {
            success: true,
            errors: []
        }
    }

    async updateDevices (id: number, form: { devices: number[]; name: string; password: number }): Promise<ResponseCustom> {
        const res = await apolloClient.mutate({
            mutation: UPDATE_DEVICES,
            variables: {
                id: id,
                input: form
            }
        })
        return {
            error: res.data.updateDevicesEmployee.error,
            msg: res.data.updateDevicesEmployee.msgError,
            success: res.data.updateDevicesEmployee.success
        }
    }
}

export default new EmployeeService()
